import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Percent, Calendar, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { CMHC_RATES, CLOSING_COSTS } from '../../data/mockData';

const MortgageCalculator = () => {
  const [homePrice, setHomePrice] = useState(650000);
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [downPaymentAmount, setDownPaymentAmount] = useState(130000);
  const [interestRate, setInterestRate] = useState(5.24);
  const [amortization, setAmortization] = useState(25);
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');
  const [includeCMHC, setIncludeCMHC] = useState(true);
  const [includeClosingCosts, setIncludeClosingCosts] = useState(true);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [isFirstTimeBuyer, setIsFirstTimeBuyer] = useState(false);

  // Update down payment amount when percent changes
  useEffect(() => {
    setDownPaymentAmount(Math.round(homePrice * (downPaymentPercent / 100)));
  }, [downPaymentPercent, homePrice]);

  // Calculate CMHC Insurance
  const calculateCMHC = () => {
    if (downPaymentPercent >= 20) return 0;
    const rate = CMHC_RATES.find(
      r => downPaymentPercent >= r.minDownPayment && downPaymentPercent <= r.maxDownPayment
    );
    if (!rate) return 0;
    const mortgageAmount = homePrice - downPaymentAmount;
    return Math.round(mortgageAmount * (rate.rate / 100));
  };

  // Calculate Ontario Land Transfer Tax
  const calculateLandTransferTax = () => {
    const rates = CLOSING_COSTS.landTransferTax.ontario;
    let tax = 0;
    let remaining = homePrice;

    // First $55,000 at 0.5%
    const tier1 = Math.min(remaining, 55000);
    tax += tier1 * 0.005;
    remaining -= tier1;

    // $55,001 to $250,000 at 1%
    if (remaining > 0) {
      const tier2 = Math.min(remaining, 250000 - 55000);
      tax += tier2 * 0.01;
      remaining -= tier2;
    }

    // $250,001 to $400,000 at 1.5%
    if (remaining > 0) {
      const tier3 = Math.min(remaining, 400000 - 250000);
      tax += tier3 * 0.015;
      remaining -= tier3;
    }

    // $400,001 to $2,000,000 at 2%
    if (remaining > 0) {
      const tier4 = Math.min(remaining, 2000000 - 400000);
      tax += tier4 * 0.02;
      remaining -= tier4;
    }

    // Over $2,000,000 at 2.5%
    if (remaining > 0) {
      tax += remaining * 0.025;
    }

    // First-time buyer rebate (up to $4,000)
    if (isFirstTimeBuyer && homePrice <= 368000) {
      tax = Math.max(0, tax - 4000);
    } else if (isFirstTimeBuyer) {
      tax = Math.max(0, tax - 4000);
    }

    return Math.round(tax);
  };

  // Calculate total closing costs
  const calculateClosingCosts = () => {
    const ltt = calculateLandTransferTax();
    const legalFees = 1800; // Average
    const titleInsurance = Math.max(300, Math.round(homePrice * 0.001));
    const homeInspection = 500;
    const appraisal = 400;
    
    return {
      landTransferTax: ltt,
      legalFees,
      titleInsurance,
      homeInspection,
      appraisal,
      total: ltt + legalFees + titleInsurance + homeInspection + appraisal,
    };
  };

  // Calculate mortgage payment
  const calculatePayment = () => {
    const cmhc = includeCMHC ? calculateCMHC() : 0;
    const mortgageAmount = homePrice - downPaymentAmount + cmhc;
    
    // Convert annual rate to period rate
    let periodsPerYear;
    switch (paymentFrequency) {
      case 'weekly': periodsPerYear = 52; break;
      case 'biweekly': periodsPerYear = 26; break;
      case 'accelerated-biweekly': periodsPerYear = 26; break;
      case 'monthly': default: periodsPerYear = 12; break;
    }

    // Canadian mortgages compound semi-annually
    const semiAnnualRate = interestRate / 100 / 2;
    const effectiveAnnualRate = Math.pow(1 + semiAnnualRate, 2) - 1;
    const periodRate = Math.pow(1 + effectiveAnnualRate, 1 / periodsPerYear) - 1;
    
    const totalPeriods = amortization * periodsPerYear;
    
    // PMT formula
    const payment = mortgageAmount * 
      (periodRate * Math.pow(1 + periodRate, totalPeriods)) / 
      (Math.pow(1 + periodRate, totalPeriods) - 1);

    return Math.round(payment * 100) / 100;
  };

  const cmhcAmount = calculateCMHC();
  const closingCosts = calculateClosingCosts();
  const payment = calculatePayment();
  const mortgageAmount = homePrice - downPaymentAmount + (includeCMHC ? cmhcAmount : 0);
  const totalCost = downPaymentAmount + (includeClosingCosts ? closingCosts.total : 0);

  const frequencyLabels = {
    weekly: 'Weekly',
    biweekly: 'Bi-Weekly',
    'accelerated-biweekly': 'Accelerated Bi-Weekly',
    monthly: 'Monthly',
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="shadow-2xl border-0">
        <CardHeader className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-3">
            <Calculator size={28} />
            <div>
              <h2 className="text-2xl font-bold">Mortgage Calculator</h2>
              <p className="text-gray-400 text-sm font-normal">Calculate your monthly payments including CMHC & closing costs</p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Inputs */}
            <div className="space-y-6">
              {/* Home Price */}
              <div>
                <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                  <DollarSign size={16} className="text-red-600" />
                  Home Price
                </Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={homePrice || ''}
                  onChange={(e) => setHomePrice(e.target.value === '' ? 0 : Number(e.target.value))}
                  className="h-12 text-lg font-semibold"
                />
                <Slider
                  value={[homePrice]}
                  onValueChange={([v]) => setHomePrice(v)}
                  min={100000}
                  max={3000000}
                  step={10000}
                  className="mt-3"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>$100K</span>
                  <span>$3M</span>
                </div>
              </div>

              {/* Down Payment */}
              <div>
                <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                  <Percent size={16} className="text-red-600" />
                  Down Payment ({downPaymentPercent}%)
                </Label>
                <div className="flex gap-3">
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={downPaymentAmount || ''}
                    onChange={(e) => {
                      const amount = e.target.value === '' ? 0 : Number(e.target.value);
                      setDownPaymentAmount(amount);
                      setDownPaymentPercent(homePrice > 0 ? Math.round((amount / homePrice) * 100) : 0);
                    }}
                    className="h-12 text-lg font-semibold flex-1"
                  />
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={downPaymentPercent || ''}
                    onChange={(e) => setDownPaymentPercent(e.target.value === '' ? 0 : Number(e.target.value))}
                    className="h-12 w-24 text-center"
                    min={5}
                    max={100}
                  />
                </div>
                <Slider
                  value={[downPaymentPercent]}
                  onValueChange={([v]) => setDownPaymentPercent(v)}
                  min={5}
                  max={50}
                  step={1}
                  className="mt-3"
                />
                {downPaymentPercent < 20 && (
                  <p className="text-amber-600 text-xs mt-2 flex items-center gap-1">
                    <Info size={12} />
                    CMHC insurance required for down payments under 20%
                  </p>
                )}
              </div>

              {/* Interest Rate */}
              <div>
                <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                  <Percent size={16} className="text-red-600" />
                  Interest Rate (%)
                </Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={interestRate || ''}
                  onChange={(e) => setInterestRate(e.target.value === '' ? 0 : Number(e.target.value))}
                  step={0.01}
                  className="h-12 text-lg font-semibold"
                />
                <Slider
                  value={[interestRate]}
                  onValueChange={([v]) => setInterestRate(Math.round(v * 100) / 100)}
                  min={1}
                  max={10}
                  step={0.01}
                  className="mt-3"
                />
              </div>

              {/* Amortization & Frequency */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Calendar size={16} className="text-red-600" />
                    Amortization
                  </Label>
                  <Select value={amortization.toString()} onValueChange={(v) => setAmortization(Number(v))}>
                    <SelectTrigger className="h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 15, 20, 25, 30].map((years) => (
                        <SelectItem key={years} value={years.toString()}>
                          {years} Years
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Payment Frequency</Label>
                  <Select value={paymentFrequency} onValueChange={setPaymentFrequency}>
                    <SelectTrigger className="h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                      <SelectItem value="accelerated-biweekly">Accelerated Bi-Weekly</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Options */}
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Include CMHC Insurance</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info size={14} className="text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">Required for down payments less than 20%. Added to your mortgage.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Switch checked={includeCMHC} onCheckedChange={setIncludeCMHC} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Include Closing Costs</Label>
                  <Switch checked={includeClosingCosts} onCheckedChange={setIncludeClosingCosts} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">First-Time Home Buyer</Label>
                  <Switch checked={isFirstTimeBuyer} onCheckedChange={setIsFirstTimeBuyer} />
                </div>
              </div>
            </div>

            {/* Right Column - Results */}
            <div className="space-y-6">
              {/* Main Payment Display */}
              <div className="bg-gradient-to-br from-red-600 to-red-700 text-white rounded-xl p-6 text-center">
                <p className="text-red-200 text-sm mb-1">{frequencyLabels[paymentFrequency]} Payment</p>
                <p className="text-5xl font-bold mb-2">
                  ${payment.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-red-200 text-sm">Over {amortization} years at {interestRate}%</p>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-100 rounded-lg p-4 text-center">
                  <p className="text-gray-500 text-xs mb-1">Mortgage Amount</p>
                  <p className="text-xl font-bold text-gray-900">
                    ${mortgageAmount.toLocaleString()}
                  </p>
                </div>
                <div className="bg-gray-100 rounded-lg p-4 text-center">
                  <p className="text-gray-500 text-xs mb-1">Total Interest</p>
                  <p className="text-xl font-bold text-gray-900">
                    ${(payment * (paymentFrequency === 'monthly' ? 12 : paymentFrequency === 'weekly' ? 52 : 26) * amortization - mortgageAmount).toLocaleString('en-CA', { maximumFractionDigits: 0 })}
                  </p>
                </div>
              </div>

              {/* CMHC */}
              {downPaymentPercent < 20 && includeCMHC && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-amber-800">CMHC Insurance</p>
                      <p className="text-xs text-amber-600">
                        {CMHC_RATES.find(r => downPaymentPercent >= r.minDownPayment && downPaymentPercent <= r.maxDownPayment)?.rate || 0}% premium rate
                      </p>
                    </div>
                    <p className="text-xl font-bold text-amber-800">${cmhcAmount.toLocaleString()}</p>
                  </div>
                </div>
              )}

              {/* Closing Costs Breakdown */}
              {includeClosingCosts && (
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowBreakdown(!showBreakdown)}
                    className="w-full p-4 flex justify-between items-center hover:bg-gray-100 transition-colors"
                  >
                    <div>
                      <p className="font-semibold text-gray-900">Estimated Closing Costs</p>
                      <p className="text-xs text-gray-500">Click to {showBreakdown ? 'hide' : 'view'} breakdown</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-xl font-bold text-gray-900">${closingCosts.total.toLocaleString()}</p>
                      {showBreakdown ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </button>
                  
                  {showBreakdown && (
                    <div className="border-t p-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Land Transfer Tax (ON)</span>
                        <span className="font-medium">${closingCosts.landTransferTax.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Legal Fees</span>
                        <span className="font-medium">${closingCosts.legalFees.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Title Insurance</span>
                        <span className="font-medium">${closingCosts.titleInsurance.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Home Inspection</span>
                        <span className="font-medium">${closingCosts.homeInspection.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Appraisal</span>
                        <span className="font-medium">${closingCosts.appraisal.toLocaleString()}</span>
                      </div>
                      {isFirstTimeBuyer && (
                        <p className="text-green-600 text-xs pt-2 border-t">
                          * First-time buyer rebate applied to Land Transfer Tax
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Total Upfront */}
              <div className="bg-gray-900 text-white rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold">Total Cash Needed</p>
                    <p className="text-xs text-gray-400">Down payment + closing costs</p>
                  </div>
                  <p className="text-2xl font-bold">${totalCost.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MortgageCalculator;
